import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { MOODS } from '@racksavant/core';
import { PLANS } from '@racksavant/ui';
import { cx } from '../cx';
import { Reveal, Wipe } from '../components/Reveal';
import { ChaosField } from '../components/ChaosField';
import { Cutout, Plate } from '../components/Photo';
import { FeatureArt } from '../components/FeatureArt';
import { Faq, type FaqItem } from '../components/Faq';
import { ArrowRight, Check, Dash } from '../components/icons';
import ui from '../ui.module.css';
import s from './CreatorsPage.module.css';

/**
 * The analytics panel is an illustration of the Studio report, not a report.
 *
 * It used to carry invented figures — "1,904 followers fitted the drop", a bar
 * chart with made-up widths. Those read as measurements, and a measurement
 * nobody took is a lie. What is left is the true part: which columns the report
 * has, and which garment sits in which row. No values, no bars.
 */
const REPORT_ROWS = [
  { name: 'Oxblood trench', mood: MOODS.regal },
  { name: 'Mesh long sleeve', mood: MOODS.futuristic },
  { name: 'Column skirt', mood: MOODS.minimal },
  { name: 'Carpenter jean', mood: MOODS.grunge },
];

/** What Studio counts per garment. The counts themselves are yours, not ours. */
const REPORT_COLUMNS = ['Fittings', 'Hype', 'Saved to an era'];

const MONEY = [
  {
    title: 'Brand drops',
    body: 'A label sends you the season. You publish it as a drop your followers can put themselves in, and the brand gets try-ons instead of impressions.',
    note: 'Sold as a line item, not as a post',
  },
  {
    title: 'Paid lookbooks',
    body: 'An era, published behind whatever you already use to charge — a members tier, a one-off sale, a newsletter. Eight looks and the reasoning behind them.',
    note: 'Repeatable monthly',
  },
  {
    title: 'Styling sessions',
    body: 'Open a fitting room with a client in it. You pull, they react, everything is decided inside an hour instead of over three weeks of voice notes.',
    note: 'Charge by the room',
  },
  {
    title: 'Concepting before a shoot',
    body: 'Test twelve directions on your own photo before anyone books a studio. Turn up with a decision instead of a mood board.',
    note: 'Saves the day you were going to lose',
  },
  {
    title: 'Affiliate edits that convert',
    body: 'People do not click a link because it is a nice jacket. They click because they have already seen it on themselves.',
    note: 'Higher intent than a flat lay',
  },
  {
    title: 'Content packages for brands',
    body: 'Deliver a set of looks, captions and cut-to-format exports. Commercial licence on Studio means you can hand them over.',
    note: 'Deliverables, not vibes',
  },
];

const LICENCE = [
  {
    ok: true,
    text: 'Use looks you generate in paid work — ads, sponsored posts, client deliverables.',
  },
  { ok: true, text: 'Upload garments you own or are authorised to use, and publish them as drops.' },
  { ok: true, text: 'Sell access to lookbooks and eras you have made.' },
  { ok: true, text: 'Keep everything if you cancel. Your looks are yours; export before you go.' },
  {
    ok: false,
    text: 'Upload a photo of someone else without their consent. Not on any plan, ever.',
  },
  {
    ok: false,
    text: 'Present a generated look as an unedited photograph in a context where that misleads.',
  },
];

const FAQS: FaqItem[] = [
  {
    q: 'Will it change how I look?',
    a: [
      'No. The image model is instructed to preserve your face, body, pose, skin and age, and to change only the clothing. That instruction is in the pipeline, not in a settings toggle.',
      'Nothing in the product ever comments on your body either. Mood reads, captions and Savant all talk about garments. There is no flattering language anywhere in the copy, because the whole product is built on the assumption that you did not ask.',
    ],
  },
  {
    q: 'Do you train on my photos?',
    a: [
      'No. Your photos are inputs to your fittings. They are not used to train our models and they are not sold, licensed or handed to anyone else for training.',
      'Deleting a body shot deletes every look generated from it in the same operation. There is no soft-delete tier where the images quietly persist.',
    ],
  },
  {
    q: 'Can I upload my own clothes?',
    a: [
      'On Studio, yes. Upload flat lays or product shots and they join your private rack immediately. You can keep them private, use them in your own fittings, or publish them as a drop.',
      'You need the right to use the images you upload — your own product photography, or shots a brand has cleared for you.',
    ],
  },
  {
    q: 'What do my followers need in order to open a drop?',
    a: [
      'A free account and one photo. Free accounts get eight fittings a day, which is more than enough to work through a drop.',
      'A drop link is public, so it can go anywhere: stories, bio, a newsletter, a QR code at an event.',
    ],
  },
  {
    q: 'Can I use these images in paid brand work?',
    a: [
      'Yes, on Studio. That plan includes a commercial licence covering the looks you generate, including client deliverables and paid social.',
      'Where a look features a brand’s garment, you still need whatever permission that brand normally requires — the licence covers our side, not theirs.',
    ],
  },
  {
    q: 'Do you watermark?',
    a: [
      'Free looks carry a small mark. Muse and Studio exports do not.',
      'Story exports can optionally burn in the mood read, which is a design choice you control rather than a watermark.',
    ],
  },
  {
    q: 'Is there an API?',
    a: [
      'Not publicly yet. If you are running a team or an agency workflow and need one, write to hello@racksavant.com and tell us what you would build.',
    ],
  },
  {
    q: 'What happens if I cancel?',
    a: [
      'You drop to Free at the end of the period you have already paid for. Your looks, lookbooks and drops stay in your account.',
      'Uploaded garments stay too, but publishing new drops and the commercial licence are Studio features, so those stop.',
    ],
  },
];

export function CreatorsPage() {
  return (
    <>
      {/* ---------------------------------------------------------- Hero */}
      <section className={s.hero} aria-labelledby="creators-title">
        <div className={cx(ui.container, s.heroGrid)}>
          <div className={s.heroCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>For creators</p>

            <h1 id="creators-title" className={s.heroTitle}>
              <Wipe
                lines={[
                  <span key="a" className={s.heroSmall}>
                    Your feed, before you
                  </span>,
                ]}
              />
              <ChaosField text="shoot it" italic className={s.heroChaos} />
            </h1>

            <p className={ui.lede}>
              If people look at you for a living, the expensive thing is not the clothes. It is the
              Saturday you lose to a shoot that was never going to work, and the eleven drafts you
              never posted because the idea was only eighty percent right.
            </p>
            <p className={ui.body}>
              Studio is the plan for that. Upload your own pieces, test the idea on your own photo,
              publish drops your audience can wear, and see which of your instincts actually landed.
            </p>
            <div className={s.heroCtas}>
              <Link to="/auth" className={cx(ui.btn, ui.primary)}>
                Go Studio
                <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className={cx(ui.btn, ui.ghost)}>
                Compare plans
              </Link>
            </div>
          </div>

          <div className={s.heroArt}>
            <Plate id="group" caption={false} ratio={3 / 4} />
            <div className={s.heroAside}>
              <p className={s.asideHead}>
                <span>{PLANS.studio.name}</span>
                <span className={s.asidePrice}>
                  {PLANS.studio.price}
                  <em>{PLANS.studio.period}</em>
                </span>
              </p>
              <ul className={s.asideList}>
                {PLANS.studio.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <p className={ui.small}>{PLANS.studio.line} Yearly billing gives you two months back.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- Garments */}
      <section className={ui.section} aria-labelledby="garments-title">
        <div className={cx(ui.container, s.row)}>
          <Reveal className={s.rowCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Your own rack</p>
            <h2 id="garments-title" className={ui.h2}>
              Upload the pieces that are actually yours.
            </h2>
            <p className={ui.body}>
              A shared catalogue is fine for discovering a silhouette. It is useless when the whole
              point of your account is a specific archive — your samples, your vintage, the four
              things a brand sent you last week.
            </p>
            <p className={ui.body}>
              Studio lets you put those in. Flat lays and product shots both work. They land in a
              private rack that only you can fit against, until you decide otherwise.
            </p>
            <ul className={s.list}>
              <li>Flat lay or on-model source images, up to a few hundred pieces.</li>
              <li>Tag them with moods and occasions so they behave like the rest of the rack.</li>
              <li>Private by default. Publishing is a separate, deliberate act.</li>
            </ul>
          </Reveal>
          <Reveal className={s.rowArt} delay={90}>
            <FeatureArt kind="rack" />
          </Reveal>
        </div>
      </section>

      {/* ---------------------------------------------------------- Drops */}
      <section className={ui.section} aria-labelledby="drops-title">
        <div className={cx(ui.container, s.row, s.rowFlip)}>
          <Reveal className={s.rowCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Drops</p>
            <h2 id="drops-title" className={ui.h2}>
              Give people the edit, not the link.
            </h2>
            <p className={ui.body}>
              A link in bio asks your audience to imagine themselves in something. A drop just shows
              them. They open it, add their photo, and try the whole set on — which is a completely
              different act from tapping through to a product page.
            </p>
            <p className={ui.body}>
              For you it is the fastest read you will ever get on your own taste. You find out which
              of the eight pieces people actually wanted before anybody has spent money on anything.
            </p>
            <ul className={s.list}>
              <li>Publish to your followers, or to anyone with the link.</li>
              <li>Every fitting from your drop is attributed back to you.</li>
              <li>Close a drop whenever you like. It stops accepting fittings and stays readable.</li>
            </ul>
          </Reveal>
          <Reveal className={s.rowArt} delay={90}>
            <FeatureArt kind="drops" />
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------------ Analytics */}
      <section className={ui.section} aria-labelledby="analytics-title">
        <div className={cx(ui.container, s.row)}>
          <Reveal className={s.rowCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Analytics</p>
            <h2 id="analytics-title" className={ui.h2}>
              Taste, with a feedback loop attached.
            </h2>
            <p className={ui.body}>
              Engagement numbers tell you a post did well. They do not tell you which of the six
              things in the post was the reason. Studio breaks it down to the garment: fittings,
              hype, saves, and the moods people chose when they tried it.
            </p>
            <p className={ui.body}>
              After two or three drops it stops being reporting and starts being a brief. You can
              see the difference between what you like and what your audience wants from you, which
              is a genuinely useful thing to know before you sign a contract.
            </p>
            <Plate id="street" className={s.rowPlate} ratio={16 / 9} focus="50% 35%" />
          </Reveal>

          <Reveal className={s.rowArt} delay={90}>
            <div className={s.analytics}>
              <p className={s.analyticsHead}>Per drop, per garment</p>
              <ul className={s.metricRow}>
                {REPORT_COLUMNS.map((column) => (
                  <li key={column} className={s.metric}>
                    <span className={s.metricLabel}>{column}</span>
                  </li>
                ))}
              </ul>
              <ul className={s.bars}>
                {REPORT_ROWS.map((row) => (
                  <li key={row.name} className={s.barRow}>
                    <span
                      className={s.rowSwatch}
                      style={{ '--c': row.mood.color } as CSSProperties}
                      aria-hidden="true"
                    />
                    <span>{row.name}</span>
                    <span className={s.rowMood}>{row.mood.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* --------------------------------------------- What you can charge */}
      <section className={cx(ui.section, s.money)} aria-labelledby="money-title">
        <div className={ui.container}>
          <Reveal className={s.moneyHead}>
            <div>
              <span className={ui.crop} aria-hidden="true" />
              <p className={ui.eyebrow}>The commercial bit</p>
              <h2 id="money-title" className={cx(ui.h2, ui.misreg)}>
                What you can actually charge for.
              </h2>
              <p className={ui.lede}>
                Six things creators are already doing with this. None of them require an audience of
                a million people. Most of them require an audience that trusts your eye.
              </p>
            </div>
            <Cutout id="jacket" width={200} tilt={-4} className={s.moneyCutout} />
          </Reveal>

          <ol className={s.moneyGrid}>
            {MONEY.map((item, i) => (
              <li key={item.title}>
                <Reveal className={s.moneyCard} delay={i * 60}>
                  <span className={s.moneyIndex}>{String(i + 1).padStart(2, '0')}</span>
                  <h3 className={s.moneyTitle}>{item.title}</h3>
                  <p className={s.moneyBody}>{item.body}</p>
                  <p className={s.moneyNote}>{item.note}</p>
                </Reveal>
              </li>
            ))}
          </ol>

          <p className={s.disclaimer}>
            We are not going to print rate cards. What you can charge depends on your audience, your
            niche and your nerve, and anyone publishing guaranteed numbers is selling something.
            What Studio gives you is the licence to do the work and the data to price it honestly.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------- Licence */}
      <section className={ui.section} aria-labelledby="licence-title">
        <div className={cx(ui.container, s.licence)}>
          <div className={s.licenceCopy}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Commercial licence</p>
            <h2 id="licence-title" className={ui.h2}>
              In one screen, in English.
            </h2>
            <p className={ui.body}>
              The full wording lives in the{' '}
              <Link to="/terms" className={ui.inlineLink}>
                terms
              </Link>
              . This is the shape of it.
            </p>
            <Plate id="detail" className={s.rowPlate} ratio={4 / 3} focus="45% 35%" />
          </div>

          <ul className={s.licenceList}>
            {LICENCE.map((item) => (
              <li key={item.text}>
                {item.ok ? (
                  <Check size={16} className={s.yes} />
                ) : (
                  <Dash size={16} className={s.no} />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ------------------------------------------------------------ FAQ */}
      <section className={ui.section} aria-labelledby="creators-faq-title">
        <div className={ui.container}>
          <div className={s.faqHead}>
            <span className={ui.crop} aria-hidden="true" />
            <p className={ui.eyebrow}>Questions</p>
            <h2 id="creators-faq-title" className={ui.h2}>
              The ones people actually ask.
            </h2>
          </div>
          <Faq items={FAQS} />
        </div>
      </section>

      {/* ---------------------------------------------------------- Close */}
      <section className={s.close} aria-labelledby="creators-close">
        <Plate id="guyStreet" className={s.closePlate} caption={false} ratio={16 / 9} focus="50% 25%" />
        <div className={cx(ui.container, s.closeInner)}>
          <h2 id="creators-close" className={s.closeTitle}>
            Try the idea before you spend the day on it.
          </h2>
          <p className={s.closeLede}>
            Start free, upgrade when the work starts paying for itself. Studio is{' '}
            {PLANS.studio.price}
            {PLANS.studio.period}, cancel whenever.
          </p>
          <div className={s.closeCtas}>
            <Link to="/auth" className={cx(ui.btn, ui.primary)}>
              Open the studio
              <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className={cx(ui.btn, ui.ghost)}>
              See all plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
